/**
 * promptUploadTargets - maps a UserPromptFileSpec.assetKind (server vocabulary)
 * to the upload endpoint the UserInteractionDrawer should POST to.
 *
 * One table, no per-flow code: a new uploadable kind is one entry here plus an
 * (authorized) API route. The server names only the KIND in the prompt - never a
 * URL - so the client controls where bytes go (CLAUDE.md "Reusable primitives").
 */

import { apiFetch } from '../../../lib/apiClient';

export interface UploadedPromptAsset {
  uuid: string;
  contentUrl?: string;
  originalFileName?: string;
  /** Lets the preview switch image (thumbnail) vs document (name) rendering. */
  mimeType?: string;
  /** Drive re-import: this exact source file was already a workspace asset and was reused. */
  reused?: boolean;
}

/** Google-native editor mime types - selectable from Drive, exported server-side to a parseable format. */
const GOOGLE_DOC_MIME = 'application/vnd.google-apps.document';
const GOOGLE_SHEET_MIME = 'application/vnd.google-apps.spreadsheet';

interface UploadTarget {
  /** Multipart POST endpoint (field name 'file'). */
  endpoint: string;
  /** Client-side cap, mirrored server-side. */
  maxBytes: number;
  /**
   * Google Drive import support. When present, the drawer offers "Import from Google Drive"
   * for this kind (a design-aligned alternative to device upload). `extraDriveMimeTypes` adds
   * Google-native editor types (Docs/Sheets) the server EXPORTS on download - they aren't in
   * the device `accept` list but are selectable from Drive. The import always lands via the
   * workspace-admin `/assets/from-drive` route keyed by kind, so only workspace-scoped kinds
   * (not deal-scoped) opt in here.
   */
  drive?: { extraDriveMimeTypes: string[] };
}

const UPLOAD_TARGETS: Record<string, UploadTarget> = {
  // Admin-gated: workspace logos / brand images (workspace.asset kind 'brand_asset').
  brand_asset: {
    endpoint: '/api/workspace/admin/assets',
    maxBytes: 5 * 1024 * 1024,
    drive: { extraDriveMimeTypes: [] },
  },
  // Admin-gated: example SOW documents (.docx/.pdf) for AI template generation.
  // This client value is a hard ceiling; the operative cap travels in the
  // prompt's UserPromptFileSpec.maxBytes (workspace config ai.sow_example_max_bytes).
  sow_example: {
    endpoint: '/api/workspace/admin/assets?kind=sow_example',
    maxBytes: 25 * 1024 * 1024,
    // A Google Doc exports to .docx, which the SOW analyzer accepts.
    drive: { extraDriveMimeTypes: [GOOGLE_DOC_MIME] },
  },
  // Deal-scoped: a meeting transcript uploaded during the /create-deal wizard.
  // The endpoint is per-deal (':dealUuid' is substituted from the prompt's
  // UserPromptFileSpec.dealUuid) and returns the knowledge.document uuid in the
  // { asset:{uuid} } envelope, so the ask_user file answer's assetUuid IS the
  // transcriptDocUuid the deal_transcript_analysis skill consumes.
  // (No Drive import yet: the per-deal endpoint differs from the from-drive route - deferred.)
  deal_transcript: {
    endpoint: '/api/sales/deals/:dealUuid/transcript-asset',
    maxBytes: 25 * 1024 * 1024,
  },
  // Admin-gated: past improvement-estimator files for the reference corpus. Accepts the
  // estimate-bearing family (.xlsx/.xls/.xlsm/.xlsb/.ods/.csv/.docx/.doc/.pdf) - see
  // REFERENCE_ESTIMATE_MIME_TYPES in @protoss/shared and the parser in reference-estimate-parse.ts.
  reference_estimate: {
    endpoint: '/api/workspace/admin/assets?kind=reference_estimate',
    maxBytes: 25 * 1024 * 1024,
    // A Google Sheet exports to .xlsx and a Google Doc to .docx - both in the estimate family.
    drive: { extraDriveMimeTypes: [GOOGLE_SHEET_MIME, GOOGLE_DOC_MIME] },
  },
};

export function getUploadTarget(assetKind: string): UploadTarget | undefined {
  return UPLOAD_TARGETS[assetKind];
}

/** True when this asset kind can be imported from Google Drive (drawer shows the button). */
export function supportsDriveImport(assetKind: string): boolean {
  return !!UPLOAD_TARGETS[assetKind]?.drive;
}

/**
 * Mime types to restrict the Drive file Picker to for an asset kind: the device `accept` list
 * (server-provided per prompt) plus the kind's Google-native editor types. Empty ⇒ allow any file.
 */
export function driveMimeTypes(assetKind: string, specAccept: string[] | undefined): string[] {
  const extra = UPLOAD_TARGETS[assetKind]?.drive?.extraDriveMimeTypes ?? [];
  return [...new Set([...(specAccept ?? []), ...extra])];
}

export interface DrivePickedFile {
  id: string;
  name: string;
  mimeType: string;
}

/**
 * Import one or more Drive-picked files as workspace assets for a prompt answer. Returns the
 * created assets (indistinguishable from device-uploaded ones) plus any per-file failures so the
 * drawer can report which files couldn't be read without dropping the rest.
 */
export async function assetsFromDrive(
  assetKind: string,
  integrationUuid: string,
  files: DrivePickedFile[],
  /** Bypass re-import dedup and create fresh copies (the "import a fresh copy" override). */
  force = false
): Promise<{ assets: UploadedPromptAsset[]; errors: { name: string; code: string }[] }> {
  const target = getUploadTarget(assetKind);
  if (!target?.drive) {
    throw new Error(`Drive import is not supported for "${assetKind}".`);
  }
  return apiFetch<{ assets: UploadedPromptAsset[]; errors: { name: string; code: string }[] }>(
    `/api/workspace/admin/assets/from-drive?kind=${encodeURIComponent(assetKind)}`,
    { method: 'POST', body: JSON.stringify({ integrationUuid, files, force }) }
  );
}

const UPLOAD_ERROR_MESSAGES: Record<string, string> = {
  UNSUPPORTED_IMAGE_TYPE: 'Unsupported image type. Use PNG, JPG, SVG, or WebP.',
  UNSUPPORTED_DOCUMENT_TYPE:
    'Unsupported file type. Use one of the formats listed for this upload.',
  FILE_TOO_LARGE: 'The file is too large.',
  EMPTY_FILE_UPLOAD: 'The file is empty.',
};

export function uploadErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : '';
  return UPLOAD_ERROR_MESSAGES[raw] ?? 'Upload failed. Please try again.';
}

/** Upload one file for a prompt answer; returns the created workspace asset. */
export async function uploadPromptFile(
  assetKind: string,
  file: File,
  opts?: { dealUuid?: string }
): Promise<UploadedPromptAsset> {
  const target = getUploadTarget(assetKind);
  if (!target) {
    throw new Error(`No upload target is configured for "${assetKind}".`);
  }
  if (file.size > target.maxBytes) {
    throw new Error('FILE_TOO_LARGE');
  }
  // Entity-scoped endpoints carry a ':dealUuid' placeholder filled from the prompt spec.
  let endpoint = target.endpoint;
  if (endpoint.includes(':dealUuid')) {
    if (!opts?.dealUuid) {
      throw new Error(`Upload kind "${assetKind}" requires a dealUuid but none was provided.`);
    }
    endpoint = endpoint.replace(':dealUuid', encodeURIComponent(opts.dealUuid));
  }
  const formData = new FormData();
  formData.set('file', file);
  const result = await apiFetch<{ asset: UploadedPromptAsset }>(endpoint, {
    method: 'POST',
    body: formData,
  });
  return result.asset;
}
